<template>
  <dp-loading v-if="loading" />
  <BaseContainer v-else :class="{ 'disable-all': removeItem.loading || addItem.loading }">
    <section v-if="order" id="basket-header">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <h1 class="text-center h2" v-html="$t('views.basket.yourBasket', [order.itemsCount || 0, $n(order.total || 0, 'currency')])" />
      <h2 v-if="order.itemsCount > 0" class="text-secondary text-center h4">
        <b-link href="javascript:void(0)" @click="checkoutClick"><u>Checkout Now</u></b-link>
      </h2>
      <hr />
    </section>

    <section v-if="!order || !order.items || !order.items.length" id="basket-empty" class="text-center">
      <div>
        <fa-icon :icon="['far', 'shopping-basket']" />
        <h2>{{ $t('views.basket.noItems') }}</h2>
        <b-button class="mt-3" variant="primary" :href="storeLink" block>{{ $t('views.basket.shopNow') }}</b-button>
      </div>
    </section>

    <template v-else>
      <section v-if="pets && pets.length" id="basket-pets">
        <div v-for="pet in pets" :key="pet.id" class="mb-4">
          <pet-header :pet="pet"></pet-header>
          <product-header v-if="sections.pets[pet.id].licenses.length">
            <template #icon> <fa-icon :icon="['far', 'address-card']" class="icon mr-1" /> </template>
            <template #title>{{ $t('views.basket.licenseTitle') }}</template>
            <template v-if="hasPenaltyFee" #tooltip>{{ $t('views.basket.tooltip.penaltyFee') }}</template>
          </product-header>
          <product
            v-for="(item, index) in sections.pets[pet.id].licenses"
            :key="item.id"
            :item="item"
            :last="index + 1 == sections.pets[pet.id].licenses.length"
            :licensing-total="licensingTotalByPet(pet.id)"
          />

          <product-header v-if="sections.pets[pet.id].replacements.length">
            <template #icon> <fa-icon :icon="['far', 'sync-alt']" class="icon mr-1" /> </template>
            <template #title>{{ $t('views.basket.replacementTitle') }}</template>
          </product-header>
          <product
            v-for="(item, index) in sections.pets[pet.id].replacements"
            :key="item.id"
            :item="item"
            :last="index + 1 == sections.pets[pet.id].replacements.length"
          />
          <div
            v-if="containFreePetTagWithReplacementFee(pet.id)"
            class="px-1.5 py-2 bg-warning-light rounded col-12 align-self-center text-primary my-2"
          >
            {{ freePetTagGroupNotification(pet.id) }}
          </div>
          <product-header v-if="sections.pets[pet.id].tags.length">
            <template #icon> <fa-icon :icon="['far', 'tags']" class="icon mr-1" /> </template>
            <template #title>{{ $t('views.basket.tagTitle') }}</template>
          </product-header>
          <product
            v-for="(item, index) in sections.pets[pet.id].tags"
            :key="item.id"
            :class="{ 'pb-3': sections.pets[pet.id].collars.length }"
            :item="item"
            :no-new-tag="noNewTag(item)"
            :last="index + 1 == sections.pets[pet.id].tags.length"
            :remove="removeTag(item, pet)"
            :edit="editTag(item, pet)"
            @remove-bundle="removeBundle"
          />
          <product-header v-if="sections.pets[pet.id].collars.length || sections.pets[pet.id].petTrackingDevices.length">
            <template #icon> <fa-icon :icon="['far', 'shopping-basket']" class="icon mr-1" /> </template>
            <template #title>{{ $t('views.basket.petProductTitle') }}</template>
          </product-header>
          <dp-product-messaging v-if="sections.pets[pet.id].petTrackingDevices.length">
            <template #icon>
              <svg width="33" height="28" viewBox="0 0 33 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.5995 27.303L3.04397 16.5148C2.77539 16.2654 2.51959 15.9968 2.28298 15.7218H7.84654C9.29178 15.7218 10.5963 14.8521 11.1527 13.5156L11.8242 11.904L14.9768 18.9065C15.2199 19.45 15.7506 19.8017 16.3454 19.8081C16.9401 19.8145 17.4836 19.4884 17.7522 18.9576L20.4637 13.5283L20.5724 13.7458C21.1799 14.9608 22.4205 15.7282 23.7762 15.7282H30.4589C30.2223 16.0032 29.9665 16.2718 29.6979 16.5212L18.1423 27.303C17.6627 27.7506 17.0296 28 16.3709 28C15.7123 28 15.0792 27.7506 14.5995 27.303ZM32.2111 12.6522H23.7698C23.578 12.6522 23.3989 12.5435 23.3094 12.3709L21.8258 9.41003C21.5636 8.89205 21.0328 8.55951 20.4509 8.55951C19.8689 8.55951 19.3382 8.88565 19.076 9.41003L16.4285 14.705L13.1671 7.42122C12.9177 6.86486 12.3549 6.50675 11.7474 6.51954C11.1399 6.53233 10.5899 6.89684 10.3533 7.46598L8.31976 12.3453C8.24302 12.5371 8.05118 12.6586 7.84654 12.6586H1.02318C0.856916 12.6586 0.703439 12.6842 0.556356 12.729C0.191847 11.7058 0 10.6187 0 9.51235V9.14145C0 4.67142 3.22942 0.860056 7.63551 0.124643C10.5516 -0.361369 13.5188 0.591471 15.6035 2.67621L16.3709 3.44359L17.1383 2.67621C19.2231 0.591471 22.1903 -0.361369 25.1064 0.124643C29.5124 0.860056 32.7419 4.67142 32.7419 9.14145V9.51235C32.7419 10.5931 32.5628 11.6546 32.2111 12.6522Z" fill="#003C71"/>
              </svg>
            </template>
            <template #message>
              <span v-html="$t('views.basket.petTrackerAddedMessaging', { petName: pet.name })"></span>
            </template>
          </dp-product-messaging>
          <product
            v-for="(item, index) in [...sections.pets[pet.id].collars, ...sections.pets[pet.id].petTrackingDevices]"
            :key="item.id"
            :item="item"
            :no-new-tag="false"
            :last="index + 1 === [...sections.pets[pet.id].collars, ...sections.pets[pet.id].petTrackingDevices].length"
            :remove="removeTag(item, pet)"
            :edit="editTag(item, pet)"
            @remove-pet-tracker="removePetTracker"
          />
          <hr />
          <pet-footer :pet="pet" :bundle-count="bundleCountByPetId[pet.id]" :order-id="orderId"></pet-footer>

          <div v-if="showWalletCardProducts && hasWalletCardItemsOrConfiguration(pet.id)">
            <product-header class="mt-2">
              <template #icon> <fa-icon :icon="['far', 'address-card']" class="icon mr-1" /> </template>
              <template #title>{{ $t('views.basket.walletCardProductTitle') }}</template>
            </product-header>
            <template>
              <product
                v-for="(item, index) in sections.pets[pet.id].walletcards"
                :key="item.id"
                :class="{ 'pb-3': sections.pets[pet.id].walletcards.length }"
                :item="item"
                :last="index + 1 === sections.pets[pet.id].walletcards.length"
                remove
                wallet-card
                @remove-bundle="removeBundle"
              />
            </template>
            <configure-wallet-card
              v-for="({ orderItemBundle }, index) in incompleteWalletCardConfigurations(pet.id)"
              :key="`incomplete-wallet-card-configuration-${index}`"
              :order-item-bundle="orderItemBundle"
              :pet-id="pet.id"
              :pet-name="pet.name"
              :wallet-card-complete="false"
              remove
              @remove-bundle="removeBundle"
            />
          </div>
        </div>
      </section>

      <section v-if="sections.essentials.length || sections.miscellaneous.length" id="miscellaneous-products" class="mb-4">
        <product-header>
          <template #icon> <fa-icon :icon="['far', 'shopping-basket']" class="icon mr-1" /> </template>
          <template #title>{{ $t('views.basket.productTitle') }}</template>
        </product-header>
        <product
          v-for="(item, index) in [...sections.essentials, ...sections.miscellaneous]"
          :key="item.id"
          :item="item"
          :last="index + 1 === [...sections.essentials, ...sections.miscellaneous].length"
          remove
          additional
        />
        <hr />
      </section>

      <section v-if="sections.walletCardConfigurations.length" class="mb-4">
        <product-header>
          <template #icon>
            <fa-icon :icon="['far', 'address-card']" class="icon mr-1" />
          </template>
          <template #title>{{ $t('views.basket.walletCardProductTitle') }}</template>
        </product-header>
        <configure-wallet-card
          v-for="({ orderItemBundle }, index) in sections.walletCardConfigurations"
          :key="`incomplete-wallet-card-configuration-${index}`"
          :order-item-bundle="orderItemBundle"
          :wallet-card-complete="false"
          @remove-bundle="removeBundle"
        />
        <hr />
      </section>

      <section v-if="sections.additional.length" id="additional-products" class="mb-4">
        <product-header>
          <template #icon> <fa-icon :icon="['far', 'money-check-edit-alt']" class="icon mr-1" /> </template>
          <template #title>{{ $t('views.basket.additionalFeesTitle') }}</template>
        </product-header>
        <product
          v-for="(item, index) in sections.additional"
          :key="item.id"
          :item="item"
          :last="index + 1 === sections.additional.length"
          additional
        />
        <hr />
      </section>

      <section v-if="sections.donations.length" id="donation-products" class="mb-4">
        <product-header>
          <template #icon> <fa-icon :icon="['far', 'heart']" class="icon mr-1" /> </template>
          <template #title>{{ $t('views.basket.donationTitle') }}</template>
        </product-header>
        <product
          v-for="(item, index) in sections.donations"
          :key="item.id"
          :item="item"
          :last="index + 1 == sections.donations.length"
          remove
        />
        <hr />
      </section>

      <section v-if="sections.vs.pet.length || sections.vs.petOwner.length" id="vs-products">
        <h2 class="mb-2">
          <strong>{{ $t('views.basket.vsTitle') }}</strong>
        </h2>
        <product-header v-if="sections.vs.pet.length">
          <template #icon> <fa-icon :icon="['far', 'eye']" class="icon mr-1" /> </template>
          <template #title>{{ $t('views.basket.vsPetTitle') }}</template>
        </product-header>
        <product v-for="(item, index) in sections.vs.pet" :key="item.id" :item="item" :last="index + 1 == sections.vs.pet.length" vs />
        <product-header v-if="sections.vs.petOwner.length">
          <template #icon> <fa-icon :icon="['far', 'eye']" class="icon mr-1" /> </template>
          <template #title>{{ $t('views.basket.vsGuardianTitle') }}</template>
        </product-header>
        <product
          v-for="(item, index) in sections.vs.petOwner"
          :key="item.id"
          :last="index + 1 == sections.vs.petOwner.length"
          :item="item"
          vs
        />
        <hr />
      </section>

      <section v-if="!orderId || !order" id="additional-info">
        <h3>Remember, you'll need to create a DocuPet account to take advantage of the Home<em>Safe</em><sup>&reg;</sup> 24/7 lost pet service.</h3>
      </section>

      <section id="discount-code">
        <discount />
        <hr />
      </section>

      <section id="order-summary">
        <totals can-upgrade-downgrade-shipping>
          <template #above-total>
            <h2 id="bundle-save" class="my-3 py-2 text-center" hidden>
              <a :href="storeLink" class="text-secondary">
                <span v-if="bundleCount < 3">{{ $t(`views.basket.addAnother[${bundleCount}]`) }}</span>
                <span v-else>{{ $t(`views.basket.addAnother[3]`) }}</span>
              </a>
            </h2>
          </template>
          <template #below-total>
            <h2 v-if="discountMessage" class="ml-3 mt-2 text-success">
              {{ discountMessage }}
            </h2>
          </template>
        </totals>
        <b-button :href="storeLink" class="mt-3" variant="outline-primary" block>
          {{ $t(`views.basket.continueShopping`) }}
        </b-button>
        <b-button v-if="order.id" type="submit" class="mt-3" variant="primary" block :disabled="disableCheckout" @click="checkoutClick">
          {{ $t(`views.basket.checkout`) }}
          <div v-if="prepareOrder.loading" role="status" class="fa-pulse float-right mt-1">
            <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
            <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
          </div>
        </b-button>
        <b-button v-else :href="login" class="mt-3" variant="primary" block>
          {{ $t(`views.basket.checkout`) }}
        </b-button>
        <div class="text-center mt-3">
          <b-link @click="cancelOrderClick"> Cancel Order </b-link>
        </div>

        <!-- Hidden for now -->
        <div id="order-extra-info" class="mt-4" hidden>
          <b-row align-v="center" class="pl-3">
            <b-col cols="2" class="pt-3 text-center">
              <fa-icon :icon="['far', 'truck']" size="2x" class="text-dark" />
            </b-col>
            <b-col class="pt-3">
              <p class="m-0">{{ $t(`views.basket.shipping`) }}</p>
            </b-col>
          </b-row>
          <b-row align-v="center" class="pl-3">
            <b-col cols="2" class="pt-3 text-center">
              <fa-icon :icon="['far', 'box']" size="2x" class="text-dark" />
            </b-col>
            <b-col class="pt-3">
              <p class="m-0">{{ $t(`views.basket.returnPolicy`) }}</p>
            </b-col>
          </b-row>
          <b-row align-v="center" class="pl-3">
            <b-col cols="2" class="pt-3 pb-3 text-center">
              <fa-icon :icon="['far', 'shield-check']" size="2x" class="text-dark" />
            </b-col>
            <b-col class="pt-3 pb-3">
              <p class="m-0">{{ $t(`views.basket.warranty`) }}</p>
            </b-col>
          </b-row>
        </div>
      </section>

      <section id="recommended"></section>
    </template>
  </BaseContainer>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { DpLoading, DpProductMessaging } from '@docupet/component-library';
import petHeader from '@/components/basket/PetHeader.vue';
import productHeader from '@/components/basket/ProductHeader.vue';
import product from '@/components/basket/Product.vue';
import discount from '@/components/basket/Discount.vue';
import totals from '@/components/basket/Totals.vue';
import BaseContainer from '@/components/BaseContainer';
import baseLocaleUrl from '@/mixins/baseLocaleUrl';
import petFooter from '@/components/basket/PetFooter.vue';
import configureWalletCard from '@/components/basket/ConfigureWalletCard';
import { CookieMixins } from '@/mixins/cookie';

const BOULDER_CITY_ID = 3;

export default {
  name: 'Basket',
  components: {
    DpProductMessaging,
    petHeader,
    petFooter,
    productHeader,
    product,
    discount,
    totals,
    BaseContainer,
    DpLoading,
    configureWalletCard,
  },
  mixins: [baseLocaleUrl, CookieMixins],
  computed: {
    ...mapState({
      orderId: (state) => state.orderId,
      order: (state) => state.order.order,
      city: (state) => (state.order.order ? state.order.order.city : null),
      removeItem: (state) => state.order.removeItem,
      addItem: (state) => state.order.addItem,
      locale: (state) => state.locale,
      isPetOwner: (state) => state.user.user.id && state.user.user.__typename == 'PetOwner',
      isLocationBoulder: (state) => state.petOwner.petOwner.city?.id == BOULDER_CITY_ID && state.petOwner.petOwner.city?.country == 'US',
      petOwner: (state) => state.petOwner.petOwner,
      loading: (state) => state.order.loading || state.product.loading,
      prepareOrder: (state) => state.order.prepareOrder,
    }),
    ...mapGetters({
      pets: 'order/getPets',
      sections: 'order/getSections',
      isValidToProceed: 'order/isValid',
      renewalPetsInOrder: 'order/renewalPetsInOrder',
      licensedPetsWithoutTag: 'order/licensedPetsWithoutTag',
    }),
    bundleCount() {
      let count = 0;
      this.order?.items?.forEach((item) => {
        if (item.product.type === 'DESIGNER_TAG' && Number(item.product.price) > 0) {
          count++;
        }
      });
      return count;
    },
    bundleCountByPetId() {
      if (this.pets.length === 0) {
        return Object.assign({});
      }
      // map # of matching items to pet ID
      return this.pets.reduce(
        (accumulator, pet) => {
          accumulator[pet.id] = this.order?.items?.filter(
            (orderItem) =>
              orderItem.group?.pet?.id == pet.id && orderItem.product.type === 'DESIGNER_TAG' && Number(orderItem.product.price) > 0
          ).length;
          return accumulator;
        },
        {} // initial value, run reduce even if only 1 element in array.
      );
    },
    storeLink() {
      return `${this.baseLocaleUrl()}/store`;
    },
    invalidOrderRedirectLink() {
      if (this.licensedPetsWithoutTag.length === 0) return this.storeLink;
      return `${this.baseLocaleUrl()}/order/${this.order.id}/store/tags/pet/${this.licensedPetsWithoutTag[0]}`;
    },
    login() {
      return `${this.baseLocaleUrl()}/login/store?redirect_path=%2Fstore`;
    },
    dashboardLink() {
      return `${this.baseLocaleUrl()}/account/full/account-app`;
    },
    hasPenaltyFee() {
      return (
        this.order.items.filter((item) => {
          return item.product.subType?.toLowerCase() === 'penalty';
        }).length > 0
      );
    },
    showWalletCardProducts() {
      return Boolean(this.order?.city?.showWalletCardProducts);
    },
    petCheckoutWalletCardConfigurationUrl() {
      if (!this.order?.groups?.length) return null;

      const configurations = this.order.groups.map((group) => group.walletCardConfigurations).filter(Boolean);

      return configurations.length ? configurations.find((config) => config?.optedIn && config?.url)?.url : null;
    },
    disableCheckout() {
      return this.order?.ableToCheckout === false || this.prepareOrder.loading;
    },
    discountMessage() {
      return this.order?.discountAmountContext?.confirmationContext?.success;
    },
    hasWalletCardItemsOrConfiguration() {
      return (petId) => {
        const hasIncompleteConfigs = this.incompleteWalletCardConfigurations(petId).length > 0;
        const hasWalletCards = Boolean(this.sections.pets?.[petId]?.walletcards?.length);
        return hasIncompleteConfigs || hasWalletCards;
      };
    },
  },
  created() {
    if (this.$router.currentRoute.meta?.skipToCheckout) this.checkoutClick();
  },
  methods: {
    incompleteWalletCardConfigurations(petId) {
      const group = this.order?.groups?.find((group) => group.pet?.id === petId);

      if (!group) {
        return [];
      }

      return group.walletCardConfigurations.filter((configuration) => {
        return configuration.optedIn && !configuration.completed;
      });
    },
    async checkoutClick() {
      if (!this.orderId || !this.order) {
        window.location.href = this.login;
        return;
      }

      if (this.petCheckoutWalletCardConfigurationUrl) {
        window.location.href = `${this.petCheckoutWalletCardConfigurationUrl}?redirect_path=basket`;
        return;
      }

      if (!this.isValidToProceed) {
        window.location.href = this.invalidOrderRedirectLink;
        return;
      }

      const analyticsVars = this.getAnalyticsVars();
      const isDonation = !this.city?.skipDonation && !this.sections?.donations?.length && this.city?.partner?.product?.id;
      const name = isDonation ? 'Donation' : 'Payment';

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'begin_checkout',
          ...analyticsVars,
          location: 'user_basket',
        });
      }

      await this.$store.dispatch('order/removeDonationSuccess');
      await this.$store.dispatch('order/prepareOrderForPayment', {
        orderId: this.orderId || this.order?.id,
        paymentType: 'CC',
      });

      if (this.prepareOrder.message) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        await this.$store.dispatch(
          'notification/success',
          {
            type: 'success',
            message: this.prepareOrder.message,
            options: { dismissSecs: 15 },
          },
          { root: true }
        );
      }

      if (!this.order?.ableToCheckout || !this.order?.items?.length) {
        return;
      }

      const shouldInterrupt =
        !this.city?.noLicensing &&
        this.isPetOwner &&
        ((this.isLocationBoulder && this.petOwner?.vsLocation === 'CITY') || !this.isLocationBoulder) &&
        this.renewalPetsInOrder.length > 0;

      if (shouldInterrupt) {
        await this.$router.push({ name: 'Interrupt', params: { orderId: this.orderId, nextRoute: name } });
        return;
      }

      await this.$router.push({ name });
    },
    async cancelOrderClick() {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'remove_from_cart',
          ...this.getAnalyticsVars(),
          location: 'user_basket',
        });
      }

      if (this.orderId) {
        await this.$store
          .dispatch('order/cancelOrder', this.orderId)
          .then(() => {
            window.location.href = this.dashboardLink;
          })
          .catch(() => {});
      } else {
        this.removeBasketCookie();
        this.removeBasketCodeCookie();
        window.location.href = this.dashboardLink;
      }
    },
    noNewTag(item) {
      return item.product.name && item.product.name.toLowerCase().includes('no new tag');
    },
    removeTag(item, pet) {
      // if license and more than one tag, or replacement and not municipal
      return (
        (!this.sections.pets[pet.id].licenses.length && !this.sections.pets[pet.id].replacements.length) ||
        (!!this.sections.pets[pet.id].licenses.length && this.sections.pets[pet.id].tags.length > 1) ||
        (!!this.sections.pets[pet.id].replacements.length && !item.product.municipalTag) ||
        this.containFreePetTagWithReplacementFee(pet.id)
      );
    },
    editTag(item, pet) {
      // if license and more then one tag, or replacement and not municipal
      return !this.removeTag(item, pet) && !(!!this.sections.pets[pet.id].replacements.length && item.product.municipalTag);
    },
    getAnalyticsVars() {
      if (window.dataLayer) {
        window?.dataLayer?.push({
          ecommerce: null,
        });
        const items = [];
        this.order?.items?.forEach((item) => {
          if (item.parent) return;
          const _itemData = {
            quantity: 1,
            coupon: this.order?.discount?.code || '',
            currency: `${this.order?.city?.country || 'US'}D`,
            item_list_id: item?.product?.style?.id || '',
            item_list_name: item?.product?.style?.code?.toUpperCase() || '',
          };
          item?.children?.forEach((child) => {
            const _childData = {
              ..._itemData,
              item_id: child.product?.id || '',
              item_name: child.product?.name?.toUpperCase() || '',
              item_category: child.product?.type?.toUpperCase() || '',
              price: Number(child.subtotal || 0).toFixed(2),
            };
            if (['OTHER'].includes(child.product?.type) || (child.product?.type === 'CONNECTOR' && child.product?.id !== '207')) {
              items.push(_childData);
            }
            if (child.product?.type === 'DESIGNER_TAG' && child.keyChain) {
              items.push({
                ..._childData,
                item_id: `${item?.product?.id}_MATCHING_KEYCHAIN`,
                item_name: `${item?.product?.id}_MATCHING_KEYCHAIN`,
                item_category: 'MATCHING_KEYCHAIN',
              });
            }
          });
          if (item?.customTagOrderInfo || item?.customization) {
            items.push({
              ..._itemData,
              item_id: `${item?.product?.type?.toUpperCase()}_PERSONALIZATION`,
              item_name: `${item?.product?.type?.toUpperCase()}_PERSONALIZATION`,
              item_category: 'PERSONALIZATION',
              price: Number(item?.customization || 0).toFixed(2),
            });
          }
          items.push({
            ..._itemData,
            item_id: item?.product?.id || '',
            item_name: item?.product?.name?.toUpperCase() || '',
            item_category: item?.product?.type?.toUpperCase() || '',
            price: Number(item?.subtotal || 0).toFixed(2),
          });
        });
        return {
          ecommerce: {
            items: items,
            currency: `${this.order?.city?.country || 'US'}D`,
            value: Number(items?.reduce((p, c) => p + Number(c.price), 0)).toFixed(2),
            coupon: this.order?.discount?.code || '',
          },
        };
      }
    },
    licensingTotalByPet(petId) {
      return this.sections?.pets?.[petId]?.licenses?.reduce((acc, item) => acc + item.subtotal, 0) || 0;
    },
    containFreePetTagWithReplacementFee(petId) {
      return Boolean(this.order?.groups?.find((group) => group.pet?.id === petId)?.freePetTagContext?.containsFreePetTagWithReplacementFee);
    },
    freePetTagGroupNotification(petId) {
      return this.order?.groups?.find((group) => group.pet?.id === petId)?.freePetTagContext?.groupNotificationText;
    },
    removePetTracker() {
      window.dataLayer.push({
        event: 'pet_tracking_basket_click_remove',
      });
    },
    removeBundle() {
      window.dataLayer.push({
        event: 'basket_order_bundle_item_remove',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
#basket-empty {
  div {
    margin-top: 22vh;
    margin-bottom: 22vh;
  }
  svg {
    color: $dp-gray-2;
    width: 50%;
    height: 30%;
  }
  h2 {
    color: $dp-navy;
  }
}
#order-extra-info {
  background-color: $dp-gray-2;
  border-top: 1px solid $dp-gray-3;
  border-bottom: 1px solid $dp-gray-3;
}
#bundle-save {
  background-color: $dp-light-yellow;
}
.bg-warning-light {
  background-color: $dp-light-yellow;
}
.disable-all {
  pointer-events: none;
}
</style>
